import querySringHelper from "query-string";
import { AddressPart } from "../types/contracts/ContractAssetsExtendedSearchTypes";

const backEndBase = window.backEndBase;

export interface QueryParamType<T = string> {
  name: string;
  value: T | number | boolean;
}

export type ErrorMap = {
  [key in string]?: {
    [key in string]: any;
  }[];
};

interface PostProps<T> {
  isBlob?: boolean;
  endpoint: EndpointType;
  requestBody?: T;
  pathParams?: (string | number)[];
  queryParams?: QueryParamType[];
  formData?: FormData;
}

export interface GenericResponse<U> extends Pick<Response, "ok" | "status"> {
  body: U;
  message: string;
  file?: { blob: Blob; filename: string | undefined };
}

//two minutes
const timeoutTime: number = 120000;
//five minutes
const postTimeoutTime: number = 300000;

const post = async <T, U>({
  endpoint,
  requestBody,
  pathParams,
  queryParams,
  formData,
}: PostProps<T>): Promise<GenericResponse<U>> => {
  /* 	await new Promise(r => setTimeout(r, 5000)); */
  const queryContext = window.location.toString();
  const headers = new Headers();
  !formData && headers.set("Content-Type", "application/json");
  let path = "";
  const pathParts = Endpoint[endpoint];
  if (pathParams) {
    pathParts.forEach((part, index) => {
      path += part + "/" + (pathParams[index] ? pathParams[index] + "/" : "");
    });
    path = path.slice(0, -1);
  } else {
    path = pathParts.join("/");
  }
  let queryString = "";
  if (queryParams) {
    const queryObject: any = {};
    queryParams.forEach((param) => {
      queryObject[param.name] = param.value;
    });
    queryString = "?" + querySringHelper.stringify(queryObject);
  }

  let response: any = undefined;

  try {
    response = await fetch(`${backEndBase}/api/` + path + queryString, {
      method: "POST",
      headers,
      credentials: "include",
      body: formData || JSON.stringify(requestBody),
      signal: AbortSignal.timeout(postTimeoutTime),
    });
  } catch (error) {
    if (error instanceof Error) {
      const errorDate = String(new Date());
      if (error.name === "TimeoutError") {
        window.toErrorPage("timeout", errorDate, "POST /api/" + path + queryString, "ERR-FETCH", queryContext);
      } else if (error.name === "AbortError") {
        window.toErrorPage("abort", errorDate, "POST /api/" + path + queryString, "ERR-FETCH", queryContext);
      } else if (error.name === "TypeError") {
        window.toErrorPage("type", errorDate, "POST /api/" + path + queryString, "ERR-FETCH", queryContext);
      } else {
        window.toErrorPage("general", errorDate, "POST /api/" + path + queryString, "ERR-FETCH", queryContext);
      }
      return {
        ok: false,
        status: 500,
        body: { resultsList: [] } as U,
        message: "",
      };
    }
  }

  let body;
  let blob;
  let filename: string | undefined = undefined;
  const contentDisposition = response.headers.get("content-disposition");
  if (contentDisposition) {
    blob = await response.blob();
    try {
      filename = contentDisposition.split("filename=")[1].split(";")[0];
    } catch {
      console.warn("Unable to get filename from request");
    }
  } else {
    body = await response
      .json()
      .then((res: any) => res)
      .catch(
        () => {
          return {};
        },
        // empty body is not json and thus cannot be parsed.
        // ideally, server should not respond with an empty response body
      );
  }
  const { ok, status } = response;

  status === 401 && window.authenticate();
  if (status === 403 || status === 404 || status.toString().startsWith("50")) {
    window.toErrorPage(status, body?.timestamp, "POST /api/" + path + queryString, body?.uniquecode, queryContext);
  }

  return {
    ok,
    status,
    body: body as U,
    message: body?.message || body?.detail || "",
    ...(blob && {
      file: {
        blob,
        filename,
      },
    }),
  };
};

interface GetProps<Q = string> {
  endPoint: EndpointType;
  queryParams?: QueryParamType<Q>[];
  pathParams?: (string | number)[];
}

const get = async <U, Q = string>({ endPoint, queryParams, pathParams }: GetProps<Q>): Promise<GenericResponse<U>> => {
  /* 	await new Promise(r => setTimeout(r, 5000)); */
  const queryContext = window.location.toString();
  let path = "";
  const pathParts = Endpoint[endPoint];
  if (pathParts) {
    if (pathParams) {
      pathParts.forEach((part, index) => {
        path += part + "/" + (pathParams[index] ? pathParams[index] + "/" : "");
      });
      path = path.slice(0, -1);
    } else {
      path = pathParts.join("/");
    }
  } else {
    path = endPoint;
  }

  let queryString = "";
  if (queryParams) {
    const queryObject: any = {};
    queryParams.forEach((param) => {
      queryObject[param.name] = param.value;
    });
    queryString = "?" + querySringHelper.stringify(queryObject);
  }

  let response: any = undefined;

  const retries = 2, delay = 1000

  for (let i = 0; i < retries; i++) {
    try {
      response = await fetch(`${backEndBase}/api/` + path + queryString, {
        method: "GET",
        credentials: "include",
        signal: AbortSignal.timeout(timeoutTime),
      });
    } catch (error) {
      if (error instanceof Error) {
        const errorDate = String(new Date());
        if (error.name === "TimeoutError") {
          window.toErrorPage("timeout", errorDate, "GET /api/" + path + queryString, "ERR-FETCH", queryContext);
        } else if (error.name === "AbortError") {
          window.toErrorPage("abort", errorDate, "GET /api/" + path + queryString, "ERR-FETCH", queryContext);
        } else if (error.name === "TypeError") {
          window.toErrorPage("type", errorDate, "GET /api/" + path + queryString, "ERR-FETCH", queryContext);
        } else {
          window.toErrorPage("general", errorDate, "GET /api/" + path + queryString, "ERR-FETCH", queryContext);
        }
        return {
          ok: false,
          status: 500,
          body: { resultsList: [] } as U,
          message: "",
        };
      }
    }
    if (!response.status.toString().startsWith("50") && response.status.toString() != 408) {
      break;
    }
    if (i < retries - 1) {
      await new Promise((res) => setTimeout(res, delay * (i + 1)));
    }
  }

  let body;
  let blob;
  let filename: string | undefined = undefined;
  const contentDisposition = response.headers.get("content-disposition");
  if (contentDisposition) {
    blob = await response.blob();
    try {
      filename = contentDisposition.split("filename=")[1].split(";")[0];
    } catch {
      console.warn("Unable to get filename from request");
    }
  } else {
    body = await response
      .json()
      .then((res: any) => res)
      .catch(
        () => {
          return {};
        },
        // empty body is not json and thus cannot be parsed.
        // ideally, server should not response with an empty response body
      );
  }
  const { ok, status } = response;

  status === 401 && window.authenticate();
  if (status === 403 || status === 404 || status.toString().startsWith("50")) {
    window.toErrorPage(status, body?.timestamp, "GET /api/" + path + queryString, body?.uniquecode, queryContext);
  }

  return {
    ok,
    status,
    body: body as U,
    message: body?.message || body?.detail || "",
    ...(blob && {
      file: {
        blob,
        filename,
      },
    }),
  };
};

interface UntypedGetProps<Q = string> extends Omit<GetProps, "endPoint"> {
  endPoint: string;
}

const untypedGet = <U, Q = string>(props: UntypedGetProps<Q>): Promise<GenericResponse<U>> => {
  return get({ ...props, endPoint: props.endPoint as EndpointType });
};

//Use to catch CSP nonce from response
/* const setCSPNonce = (headers: Headers) => {
	const keys = headers.keys();
	let header = keys.next();
	while (header.value) {
		if (header.value === "content-security-policy") {
			//const csp : any = new ContentSecurityPolicy().share('json')
			//"script-src 'self';style-src 'self' 'nonce-abc123';"

			const globalNonce = headers
				.get(header.value)
				?.split(";")[1]
				.split(" ")[2]
				.split("'")[1];

			(window as any).globalNonce = globalNonce;
		}
		header = keys.next();
	}
}; */

interface PutProps<T> {
  endpoint: EndpointType;
  requestBody?: T;
  pathParams?: (string | number)[];
  queryParams?: QueryParamType[];
  formData?: FormData;
}

const put = async <T, U>({
  endpoint,
  requestBody,
  pathParams,
  queryParams,
  formData,
}: PutProps<T>): Promise<GenericResponse<U>> => {
  /* 	await new Promise(r => setTimeout(r, 5000)); */
  const queryContext = window.location.toString();
  const headers = new Headers();
  let path = "";
  const pathParts = Endpoint[endpoint];
  !formData && headers.set("Content-Type", "application/json");
  if (pathParams) {
    pathParts.forEach((part, index) => {
      path += part + "/" + (pathParams[index] ? pathParams[index] + "/" : "");
    });
    path = path.slice(0, -1);
  } else {
    path = pathParts.join("/");
  }
  let queryString = "";
  if (queryParams) {
    const queryObject: any = {};
    queryParams.forEach((param) => {
      queryObject[param.name] = param.value;
    });
    queryString = "?" + querySringHelper.stringify(queryObject);
  }

  let response: any = undefined;

  try {
    response = await fetch(`${backEndBase}/api/` + path + queryString, {
      method: "PUT",
      headers,
      credentials: "include",
      body: formData || JSON.stringify(requestBody),
      signal: AbortSignal.timeout(timeoutTime),
    });
  } catch (error) {
    if (error instanceof Error) {
      const errorDate = String(new Date());
      if (error.name === "TimeoutError") {
        window.toErrorPage("timeout", errorDate, "PUT /api/" + path + queryString, "ERR-FETCH", queryContext);
      } else if (error.name === "AbortError") {
        window.toErrorPage("abort", errorDate, "PUT /api/" + path + queryString, "ERR-FETCH", queryContext);
      } else if (error.name === "TypeError") {
        window.toErrorPage("type", errorDate, "PUT /api/" + path + queryString, "ERR-FETCH", queryContext);
      } else {
        window.toErrorPage("general", errorDate, "PUT /api/" + path + queryString, "ERR-FETCH", queryContext);
      }
      return {
        ok: false,
        status: 500,
        body: { resultsList: [] } as U,
        message: "",
      };
    }
  }

  const body = await response
    .json()
    .then((res: any) => res)
    .catch(
      () => {
        return {};
      },
      // empty body is not json and thus cannot be parsed.
      // ideally, server should not response with an empty response body
    );
  const { ok, status } = response;

  status === 401 && window.authenticate();
  if (status === 403 || status === 404 || status.toString().startsWith("50")) {
    window.toErrorPage(status, body?.timestamp, "PUT /api/" + path + queryString, body?.uniquecode, queryContext);
  }

  return {
    ok,
    status,
    body: body as U,
    message: body.message || body?.detail || "",
  };
};

interface RemoveProps {
  endpoint: EndpointType;
  pathParams?: (string | number)[];
}

const remove = async <U>({ endpoint, pathParams }: RemoveProps): Promise<GenericResponse<U>> => {
  /* 	await new Promise(r => setTimeout(r, 5000)); */
  const queryContext = window.location.toString();
  const headers = new Headers();
  headers.set("Content-Type", "application/json");
  let path = "";
  const pathParts = Endpoint[endpoint];
  if (pathParams) {
    pathParts.forEach((part, index) => {
      path += part + "/" + (pathParams[index] ? pathParams[index] + "/" : "");
    });
    path = path.slice(0, -1);
  } else {
    path = pathParts.join("/");
  }

  let response: any = undefined;

  try {
    response = await fetch(`${backEndBase}/api/` + path, {
      method: "DELETE",
      headers,
      credentials: "include",
      signal: AbortSignal.timeout(timeoutTime),
    });
  } catch (error) {
    if (error instanceof Error) {
      const errorDate = String(new Date());
      if (error.name === "TimeoutError") {
        window.toErrorPage("timeout", errorDate, "DELETE /api/" + path, "ERR-FETCH", queryContext);
      } else if (error.name === "AbortError") {
        window.toErrorPage("abort", errorDate, "DELETE /api/" + path, "ERR-FETCH", queryContext);
      } else if (error.name === "TypeError") {
        window.toErrorPage("type", errorDate, "DELETE /api/" + path, "ERR-FETCH", queryContext);
      } else {
        window.toErrorPage("general", errorDate, "DELETE /api/" + path, "ERR-FETCH", queryContext);
      }
      return {
        ok: false,
        status: 500,
        body: { resultsList: [] } as U,
        message: "",
      };
    }
  }

  const body = await response
    .json()
    .then((res: any) => res)
    .catch(
      () => {
        return {};
      },
      // empty body is not json and thus cannot be parsed.
      // ideally, server should not response with an empty response body
    );
  const { ok, status } = response;

  status === 401 && window.authenticate();
  if (status === 403 || status === 404 || status.toString().startsWith("50")) {
    window.toErrorPage(status, body?.timestamp, "DELETE /api/" + path, body?.uniquecode, queryContext);
  }

  return {
    ok,
    status,
    body: body as U,
    message: body.message || body?.detail || "",
  };
};

const Endpoint = {
  balances: ["balances"],
  balancesCSV: ["balances", "csv"],
  balancesSummary: ["balances-summary"],
  balancesSummaryCSV: ["balances-summary", "csv"],
  energyReport: ["energy-reports"],
  energyReportPublish: ["energy-reports", "publish"],
  energyReportRegenerate: ["energy-reports", "regenerate"],
  cadastral: ["cadastral"],
  configuration: ["config"],
  contract: ["contract"],
  contracts: ["contracts"],
  contractsExtended: ["contracts-extended"],
  contractsExtendedCSV: ["contracts-extended/csv"],
  contractsAssetsExtended: ["contracts-assets-extended"],
  ContractAssetsExtendedCSV: ["contracts-assets-extended/csv"],
  contractWorkspaces: ["contract", "workspaces"],
  contractWorkspace: ["contract-workspace"],
  contractWorkspaceRows: ["contract-workspace", "rows"],
  contractWorkspaceRow: ["contract-workspace", "row"],
  contractWorkspacePaymentAdministrating: ["contract-workspace", "payment-administrating"],
  contractWorkspaceRowsCsv: ["contract-workspace", "rows/csv"],
  contractDocuments: ["contract", "documents"],
  contractDocument: ["contract", "document"],
  contractDocumentDetails: ["contract-document"],
  contractSubUser: ["contract", "sub-user"],
  contractSubUserChange: ["contract", "sub-user"],
  contractObject: ["contract", "object"],
  contractObjects: ["contract", "objects"],
  contractAssets: ["contract", "assets"],
  contractFinish: ["contract", "finish"],
  contractNotAddedAssets: ["contract", "add-assets"],
  contractPaymentSchedules: ["contract", "payment-schedule"],
  contractGeneratePaymentSchedule: ["contract", "generate-payment-schedule"],
  contractPaymentScheduleRows: ["contract", "payment-schedule-rows"],
  contractPaymentScheduleRow: ["contract", "payment-schedule-row"],
  paymentScheduleRow: ["payment-schedule-row"],
  deleteUnsentRows: ["contract", "delete-unsent-rows"],
  contractPaymentScheduleData: ["contract", "payment-schedule-data"],
  contractObjectAsset: ["contract-object", "asset"],
  contractObjectDetails: ["contract-object"],
  contractFees: ["contract", "fees"],
  contractAssetDetails: ["contract-asset"],
  contractVersion: ["contract", "version"],
  contractUseDetails: ["contract", "use-details"],
  contractUserRights: ["contract", "user-rights"],
  contractContact: ["contract-contact"],
  contractVersionParty: ["contract-version", "party"],
  contractVersionPartyRenew: ["contract-version", "party/renew-data"],
  contractVersionParties: ["contract-version", "parties"],
  contractApplyVersion: ["contract-version", "apply"],
  complex: ["real-estate-complex"],
  complexCommon: ["unit-management", "real-estate-complex"],
  complexTimeline: ["real-estate-complex", "timeline"],
  complexAddAsset: ["real-estate-complex", "related-asset"],
  complexAssetPeriod: ["real-estate-complex", "asset-period"],
  complexAreaUsagePeriods: ["real-estate-complex", "indicators"],
  complexYld: ["real-estate-complex", "yldandmed"],
  complexDetail: ["real-estate-complex", "detailandmed"],
  complexDocument: ["real-estate-complex", "file"],
  complexInvestmentApplication: ["real-estate-complex", "investment-application"],
  complexBuildings: ["buildings-for-complex"],
  complexVersion: ["real-estate-complex/version"],
  complexSearch: ["real-estate-complex/search"],
  complexSearchCSV: ["real-estate-complex/csv/export"],
  complexInitiate: ["real-estate-complex", "initiate"],
  complexWithdraw: ["real-estate-complex", "withdraw"],
  complexReplenish: ["real-estate-complex", "replenish"],
  complexConfirm: ["real-estate-complex", "confirm"],
  complexTerminate: ["real-estate-complex", "terminate"],
  complexRelatedComplex: ["real-estate-complex", "parentage"],
  complexCsvImport: ["real-estate-complex/csv/import"],
  complexCsvExport: ["real-estate-complex/csv", "export"], // parameter is ID of ruler. Many entries
  complexOneExport: ["real-estate-complex-period/csv", "export"], // Single complex context
  complexPeriodMassExport: ["real-estate-complex-period/csv/export"],
  complexOneImport: ["real-estate-complex-period/csv", "import"],
  complexPeriodUnlock: ["real-estate-complex-period/csv", "unlock"],
  file: ["file"],
  maintenanceContractDetails: ["contract-version", "maintenance-details"],
  maintenanceContractWorks: ["contract-version", "maintenance-works"],
  maintenanceContractWork: ["contract-version", "work"],
  contractVersionUseDetails: ["contract-version", "use-details"],
  contractPayment: ["contract-payment"],
  contractWork: ["contract-work"],
  contractParty: ["contract-party"],
  analyses: ["analyses"],
  analysis: ["analysis"],
  interests: ["interests"],
  interest: ["interest"],
  manualAnalyses: ["manual-analyses"],
  plans: ["plans"],
  plan: ["plan"],
  priaAnalyses: ["pria-analyses"],
  asset: ["asset"],
  assets: ["assets"],
  assetsExtended: ["assets-extended"],
  assetsExtendedCSV: ["assets-extended/csv"],
  assetPartsExtended: ["asset-parts-extended"],
  assetPartsExtendedCSV: ["asset-parts-extended/csv"],
  assetcommon: ["asset-common"],
  assetmain: ["asset-main"],
  assetbuildings: ["asset-buildings"],
  assetstructures: ["asset-structures"],
  assetadministration: ["asset-administration"],
  objectContracts: ["object", "contracts"],
  assetfurtherdetails: ["asset-further-details"],
  addAssetfurtherdetails: ["asset", "new-asset-further-details"],
  editAssetfurtherdetails: ["asset", "asset-further-details"],
  assetDocuments: ["asset", "documents"],
  assetDocument: ["asset", "document"],
  assetAdministrativeDocument: ["asset", "administrative-document"],
  assetStatementPdf: ["asset", "statement"],
  updatedObject: ["asset", "update-object-composition"],
  renewAsset: ["asset", "renew"],
  assetOwnership: ["asset", "ownership"],
  assetBuilding: ["asset", "building"],
  assetStructure: ["asset", "structure"],
  assetnotificationhistory: ["asset-notification-history"],
  assetgovernances: ["asset-governances"],
  assetrelatedprocedures: ["asset-related-procedures"],
  managementModels: ["building", "management-model"],
  indoorClimate: ["building", "energy"],
  authInfo: ["auth-info"],
  accountMessages: ["account/messages"],
  accountNotifications: ["account/notifications"],
  proceedingsMessages: ["account", "proceedings-messages"],
  proceedingsInProgress: ["account", "proceedings-in-progress"],
  balanceMessages: ["account", "balance-notifications"],
  contractMessages: ["account", "contract-notifications"],
  kvvMessages: ["account", "kvv-notifications"],
  robotMessages: ["account", "robot-notifications"],
  classifiers: ["classifiers"],
  groupedProceedingTypesClassifiers: ["classifiers/grouped-proceeding-subtypes"],
  groupedContractProceedingTypesClassifiers: ["classifiers/grouped-contract-proceeding-subtypes"],
  groupedMaareformMethodToProceedingSubtypesClassifiers: ["classifiers/maareform-method-grouped-subtypes"],
  realEstateUnit: ["real-estate-complex"],
  realEstateUnitMessages: ["real-estate-complex", "messages"],
  realEstateUnitMessageDetail: ["real-estate-complex/messages"],
  realEstateComplexHomePageMessages: ["account/real-estate-complex-messages"],
  realEstateComplexParentage: ["real-estate-complex", "parentage"],
  realEstateComplexActions: ["real-estate-complex", "actions"],
  proceeding: ["proceeding"],
  proceedingBalance: ["proceeding", "balance"],
  proceedingContract: ["proceeding", "contract"],
  proceedingEvaluation: ["proceeding", "evaluation"],
  proceedingFollowUp: ["proceeding", "object", "proceeding"],
  proceedingInitiate: ["proceeding", "initiate"],
  proceedingStop: ["proceeding", "stop"],
  proceedingTerminate: ["proceeding", "terminate"],
  proceedingContinue: ["proceeding", "continue"],
  proceedingFinish: ["proceeding", "finish"],
  proceedingAsset: ["proceeding", "asset"],
  proceedingAssets: ["proceeding", "assets"],
  proceedingAssetDetails: ["proceeding", "asset", "details"],
  proceedingAssetUpdate: ["proceeding", "asset", "asset-evaluation"],
  proceedingAssetRangeOfUse: ["proceeding", "asset", "range-of-use"],
  proceedingAssetRangeOfUseKatri: ["proceeding", "asset", "range-of-use-katri"],
  proceedingDetails: ["proceeding", "details"],
  proceedingDocument: ["proceeding", "document"],
  proceedingMessages: ["proceeding", "messages"],
  proceedingObject: ["proceeding", "object"],
  proceedingObjects: ["proceeding", "objects"],
  proceedingRelation: ["proceeding", "proceeding"],
  proceedingRelations: ["proceeding", "proceedings"],
  proceedingCadastrals: ["proceeding", "related-cadastral"],
  proceedingCadastralRights: ["proceeding", "related-cadastral", "limited-rights"],
  proceedingApplication: ["proceeding", "application"],
  proceedingInspection: ["proceeding", "inspection"],
  proceedingEvaluationKatri: ["proceeding", "evaluation-katri"],
  proceedingNewObjectComposition: ["proceeding", "new-object-composition"],
  proceedingNewObject: ["proceeding", "new-object"],
  proceedingObjectDetails: ["proceeding", "object", "details"],
  proceedingObjectLimitedRights: ["proceeding", "object", "limited-rights"],
  proceedingObjectApplication: ["proceeding", "object", "application"],
  proceedingObjectApplications: ["proceeding", "object", "applications"],
  proceedingAssetAmortize: ["proceeding", "amortize"],
  proceedingRelatedAsset: ["proceeding", "related-asset"],
  proceedingProcurable: ["proceeding", "procurable"],
  proceedingMeeting: ["proceeding", "meeting"],
  proceedingArising: ["proceeding", "arising"],
  proceedingAuction: ["proceeding", "auction"],
  proceedingProcurement: ["proceeding", "procurement"],
  proceedingParty: ["proceeding", "party"],
  proceedingContact: ["proceeding", "contact"],
  proceedingLimitedRights: ["proceeding", "limited-rights"],
  role: ["role"],
  application: ["application"],
  notice: ["proceeding", "publish-notice"],
  actionHistory: ["action-history"],
  proceedings: ["proceedings"],
  proceedingsExtended: ["proceedings-extended"],
  proceedingsExtendedCSV: ["proceedings-extended", "csv"],
  proceedingsAssetsExtended: ["proceedings-assets-extended"],
  proceedingsAssetsExtendedCSV: ["proceedings-assets-extended", "csv"],
  proceedingManagers: ["organisations/proceedings"],
  proceedingNewRuler: ["organisations/new-ruler"],
  authorizedBodies: ["organisations/authorised"],
  organisationsSearch: ["organisations/search"],
  userCreators: ["user/creators"],
  usersRuler: ["users/ruler"],
  usersRepresentingOrg: ["users/representing-organisations"],
  usersByName: ["users/by-name"],
  organisation: ["organisation"],
  organisationType: ["organisation", "type"],
  organisations: ["organisations"],
  organisationsAdd: ["organisations/add"],
  organisationsRenew: ["organisations/renew"],
  organisationsExternal: ["organisations/external"],
  thi: ["consumer-price-index"],
  users: ["users"],
  user: ["user"],
  userRoleHistory: ["user", "role-history"],
  userOrgHistory: ["user", "org-history"],
  userRoles: ["user", "roles"],
  vatRate: ["vat-rate"],
  userRenew: ["user/renew"],
  newUserRepresentationRight: ["user", "mandate"],
  userRepresentationRight: ["user-mandate"],
  newUserInterestRight: ["user", "interest"],
  userInterestRight: ["user-interest"],
  userNotifications: ["user-notifications"],
  userInterestArea: ["user-interest-area"],
  ads: ["ads"],
  messagesPotentialRecipients: ["messages/potential-recipients"],
  alerts: ["alerts"],
  alert: ["alert"],
  lists: ["lists"],
  list: ["list"],
  building: ["building"],
  buildingsCsvExport: ["buildings-csv", "export"],
  buildingsCsvImport: ["buildings-csv/import"],
  unlockBuildingsOrganisation: ["buildings-csv", "unlock"],
  lockedBuildings: ["buildings-csv/locked-orgs"],
  lockedComplex: ["real-estate-complex/csv/locked-orgs"],
  structure: ["structure"],
  unlockComplexOrganisation: ["real-estate-complex/csv", "unlock"],
  unitManagementStrategy: ["unit-management", "strategy/years"],
  unitManagementStrategyList: ["unit-management", "strategy/period"],
  unitManagementStrategyChange: ["unit-management", "strategy"],
  unitManagementUsageIndicatorYear: ["unit-management", "usage-indicators/years"],
  unitManagementUsageIndicatorPeriod: ["unit-management", "usage-indicators/period"],
  unitManagementUsageIndicatorChange: ["unit-management", "usage-indicators"],
  unitManagementNonElReportablePeriod: ["unit-management", "non-el-reportable/period"],
  unitManagementNonElReportableChange: ["unit-management", "non-el-reportable"],
  unitManagementEmissionsPeriod: ["unit-management", "waste/period"],
  unitManagementEmissionsChange: ["unit-management", "waste"],
  unitManagementEnergyYearly: ["unit-management", "energy-efficiency/years"],
  unitManagementEnergyPeriod: ["unit-management", "energy-efficiency/period"],
  unitManagementEnergyChange: ["unit-management", "energy-efficiency"],
  unitManagementStages: ["unit-management", "stages"],
  unitManagementStageSingleFields: ["unit-management", "stages-dates"],
  unitManagementStateYears: ["unit-management", "condition/years"],
  unitManagementBudget: ["unit-management", "budget"],
  unitManagementPriorityChange: ["unit-management", "priority"],
  unitManagementPriorityPeriod: ["unit-management", "priority/period"],
  unitManagementStatePeriod: ["unit-management", "condition/period"],
  unitManagementStateChange: ["unit-management", "condition"],
  unitManagementRepairDebtChange: ["unit-management", "repair-debt"],
  unitManagementRepairDebtPeriod: ["unit-management", "repair-debt/period"],
  unitManagementCostOfRepairChange: ["unit-management", "cost-of-repair"],
  unitManagementCostOfRepairPeriod: ["unit-management", "cost-of-repair/period"],
  unitManagementOperationalCosts: ["unit-management", "expences/costs/years"],
  unitManagementGetSapData: ["unit-management", "expences/sap/years"],
  unitManagementPlannedCosts: ["unit-management", "expences/budget/years"],
  unitManagementCoordinatorData: ["unit-management", "coordinator-data"],
  sapImport: ["unit-management-sap/csv/import"],
  unitManagementGeneratePrognosis: ["unit-management/prognosis"],
  unitManagementSaveVersion: ["unit-management/generate-version"],
  unitManagementOverview: ["unit-management", "main/years"],
  unitManagementCsvExport: ["unit-management/csv/export"],
  unitManagementSingleCsvExport: ["unit-management/csv", "export"],
  unitManagementCsvImport: ["unit-management/csv/import"],
  rkasImport: ["investment-project-rkas/csv/import"],
  createInvestmentProjectOrMainData: ["investment-project", "main"],
  investmentProjectToArchive: ["investment-project"],
  investmentProjectPriority: ["investment-project", "priority"],
  investmentProjectSfos: ["investment-project", "sfos"],
  investmentProjectProcurement: ["investment-project", "procurements"],
  investmentProjectUser: ["investment-project", "users"],
  investmentProjectFinancialChangeOrCapitalrent: ["investment-project", "liability"],
  investmentProjectFulfillment: ["investment-project", "accounting"],
  investmentProjectBudget: ["investment-project", "budget"],
  investmentProjectFile: ["investment-project", "file"],
  investmentProjectBuilding: ["investment-project", "assets"],
  investmentProjectHomePageMessages: ["account/investment-project-messages"],
  removeInvestmentProjectHomePageMessage: ["investment-project/messages"],
  investmentProjectDecision: ["investment-project", "resolution"],
  investmentProjectDecisionFile: ["investment-project", "resolution", "file"],
  investmentProjectStatusInitiate: ["investment-project", "initiate"],
  investmentProjectStatusTakeBack: ["investment-project", "withdraw"],
  investmentProjectStatusSendBack: ["investment-project", "replenish"],
  investmentProjectStatusConfirm: ["investment-project", "confirm"],
  investmentProjectStatusSubmit: ["investment-project", "submit"],
  investmentSearch: ["investment-application/search"],
  investmentSearchCSV: ["investment-application/csv/export"],
  investmentSearchCSVWithId: ["investment-application/csv", "export"],
  investmentGeneratePdf: ["investment-application", "pdf"],
  investmentProjectGenerateNext: ["investment-project", "generate"],
  investmentProjectCsvImport: ["investment-application/csv/import"],
  unitManagementVersions: ["unit-management", "versions"],
  removeProceedingRelation: ["real-estate-complex", "parentage"],
  infotexts: ["info-buttons"],
  assessedValues: ["asset", "assessed-values"],
  saveNewWorkspace: ["contract-workspace"],
  createWorksapcePaymentScheduals: ["contract-workspace", "generate-payment-schedule"],
  proceedingCadastralCost: ["proceeding/cadastral-asset-cost"],
};

export type EndpointType = keyof typeof Endpoint;

export interface AuthResponseBody {
  isDevloginEnabled: boolean;
  authUrl: string;
  isAuthenticated: boolean;
  account: AccountResponse;
}

export interface AccountResponse {
  id: number;
  nimi: string;
  isikukood: string;
  esindusoigused: NameAndId[];
  kasutusoigused: string[];
  huviorganisatsioonid: NameAndId[];
}

export interface AssetsRequestBody {
  varaKood?: string;
  nimetus?: string;
  aadress?: AddressPart | string;
  valitsejaNimi?: string;
  volitatavNimi?: string;
  objektiLiik?: string;
  kasutuseesmark?: string;
  regOsaNumber?: string;
  katastritunnus?: string;
  ehrKood?: string; // ehrKood
  valismaal?: string;
  kinnistu?: string;
  riigiKaasomandis?: string;
  onMenetluses?: string;
  arhiivist?: string;
  offset?: number;
  limit?: number;
  kustutatud?: boolean;
  menetlusId?: string;
  orderBy?: string;
  orderByDir?: string;
}

export type Direction = "ASC" | "DESC";

export interface AssetsResponseBody {
  limit: number;
  offset: number;
  resultCount: number;
  totalCount: number;
  resultsList: Asset[];
}

export interface ProceedingRelationResponseBody {
  id: string;
  number: string;
}

export interface RealEstateComplexParentageRequestBody {
  kvyId: number;
}

export interface ProceedingCadastralRightsResponseBody {
  limit: number;
  offset: number;
  resultCount: number;
  totalCount: number;
  resultsList: ProceedingCadastralRights[];
}

export interface ProceedingCadastralRights {
  id: number;
  jagu: string;
  kandeNr: string;
  piiranguLiik: string;
  kehtivAlates: string;
  valitsevKinnisasi: string;
  oigustatudIsikud: OigustatudIsik[];
  tahtaeg: string;
  asjaoigusSeotud: string;
}

export interface OigustatudIsik {
  kood: string;
  nimi: string;
}

export interface Asset {
  id: number;
  varaKood: string;
  nimetus: string;
  aadressTekst: string;
  regOsaNumber: number;
  valitsejaNimi: string;
  volitatavNimi: string;
  objektiLiik: string;
  menetlusegaSeotud: boolean;
  arveleVoetud: string;
}

export interface ProceedingsRequestBody {
  number?: string;
  valitseja?: string;
  volitatud?: string;
  pohimenetleja?: string;
  menetluseLiik?: string;
  seisund?: string;
  menetlusAlamliik?: string;
  voorandamisviis?: string;
  oiguslikAlus?: string;
  menetluseGrupp?: string;
  uleandja?: string;
  vastuvotja?: string;
  objektiKood?: string;
  katastritunnus?: string;
  aadress?: AddressPart | string;
  alustatud?: Timerange;
  lopetatud?: Timerange;
  offset?: number;
  limit?: number;
  orderBy?: string;
  orderByDir?: string;
}

export interface ProceedingsResponseBody {
  limit: number;
  offset: number;
  resultCount: number;
  totalCount: number;
  resultsList: Proceeding[];
}

export interface ProceedingObjectsResponseBody {
  limit: number;
  offset: number;
  resultCount: number;
  totalCount: number;
  resultsList: ProceedingObject[];
}
export interface ProceedingRelationsResponseBody {
  limit: number;
  offset: number;
  resultCount: number;
  totalCount: number;
  resultsList: ProceedingRelation[];
}

export interface ProceedingCadastralsResponseBody {
  limit: number;
  offset: number;
  resultCount: number;
  totalCount: number;
  resultsList: ProceedingCadastral[];
}
export interface Proceeding {
  menetluseLiik: string;
  alamliik: string;
  algatatud: string;
  lopetatud: string;
  number: string;
  seisund: string;
  valitseja: string;
  id: number;
  volitatudAsutus: string;
  menetluseViis: string;
  oiguslikAlus: string;
  lopetamisePohjus: string;
  andjaNimi: string;
  votjaNimi: string;
  pohimenetleja: string;
}

export interface ProceedingStopRequestBody {
  peatamisePohjus: string;
}
export interface ProceedingTerminateRequestBody {
  peatamisePohjus: string;
}
export interface CreateProceedingRequestBody {
  menetluseLiik?: string;
  alamliik?: string;
  valitseja?: number;
  volitatud?: number;
  uusValitseja?: number;
  saaja?: string;
  voorandamisViis?: string;
  objektId?: string;
}

export interface CreateProceedingResponseBody {
  id?: number;
  nr?: string;
}
export interface CreateFollowUpProceedingRequestBody {
  jatkumenetluseLiik?: string;
  alamliik?: string;
  uusValitseja?: number;
  saaja?: string;
  voorandamisViis?: string;
  menetlusobjektid?: string[];
  regosaNr?: string;
  valitseja?: string;
  volitatud?: string;
}

export interface CreateFollowUpProceedingResponseBody extends Error {
  jatkumenetluseId: string;
  jatkumenetluseNr: string;
}
export interface ModifyProceedingRequestBody {
  menetlus?: {
    updateFields?: string[];
    voorandamisviis?: string;
    oiguslikAlus?: string;
    lisainfoTekst?: string;
    pohimenetleja?: string;
    arhiivitoimikuNr?: string;
    alamliik?: string;
  };
  menetlusObjekt?: {
    changes: ModifyProceedingObjectPart[];
  };
  detail?: {
    updateFields: string[];
    hoonestusoiguseNr?: string;
    kasutusviis?: string;
    kasutuseSisu?: string;
    menetluseGrupp?: string;
    menetluseEesmark?: string;
    kasutajaEelisoigus?: boolean;
    kasutajaID?: string;
    prioriteet?: string;
    viitenumber?: string;
    viimaneToiming?: string;
    kaimasolevToiming?: string;
    krundijaotuskavaNr?: string;
    maaomanikuNousolek?: boolean;
    maaomanikuMarkused?: string;
    otsustaja?: string;
    vajalikMaakorraldus?: boolean;
    kavaEdastamiseKp?: string;
    kavaKooskolastamiseKp?: string;
    solmimiseTahtaeg?: string;
    menetluseAlamseisund?: string;
    rahastaja?: string;
    vahetatavaOsaSuurus?: number;
    vahetatavaOsaVaartus?: number;
    riigileTasutavHuvitis?: string;
    maaomanikuHuvitis?: string;
    esindajaNimi?: string;
    esindajaAadress?: string;
    esindajaEpost?: string;
    esindajaKontaktnumber?: string;
    tasuAlus?: string;
    tasuTyyp?: string;
    omandamiseTasu?: number;
    motivatsiooniTasu?: number;
    asjaajamiskulud?: number;
    tyyp?: string;
    lepinguTahtajalisus?: string;
    kestvusperiood?: number;
    kestvusperioodiUhik?: string;
    lepinguAlgus?: string;
    lepinguLopp?: string;
    seotudIsikuId?: string;
    jarelmaks?: boolean;
    noudedTaidetud?: string;
    tegevusluba?: string;
    vabastamiseTahtaeg?: string;
    vabastamiseAeg?: string;
    huvitatavMaamaks?: number;
    huvitatavSumma?: number;
    tasumiseTahtaeg?: string;
    tasumiseAeg?: string;
    tulevaneValitsejaId?: string;
    tulevaneVolitatudId?: string;
    varasemMenetlusliik?: string;
  };
}

export interface ModifyProceedingObjectPart {
  id: number;
  volitatud?: number | undefined;
  valitsemiseEesmark?: string | undefined;
  eesmargiPohjus?: string | undefined;
}

export interface StatusOnlyResponseBody extends Omit<Response, "status"> {
  bodyStatus?: string;
  status: number | string;
}

export interface StatusOnlyResponseBodyWithErrorParams extends Omit<Response, "status"> {
	bodyStatus?: string;
	status: number | string;

	errorParams?: any;
}

export interface PaymentScheduleDeletedRowCountResponse {
  deletedRowCount: number;
}

export interface ProceedingResponseBody {
  id: string;
  number: string;
  menetluseLiik: string;
  alamliik: string;
  seisund: string;
  kirjeldus?: string;
  valitsejaId: number;
  valitseja: string;
  uusValitsejaId?: number;
  uusValitseja?: string;
  menetluseVolitatud?: string;
  menetluseVolitatudId?: number;
  voorandamisViis?: string;
  algatatud: string;
  lopetatud: string;
  muutmisaeg?: string;
  muutja?: string;
  vastuvotja?: string;
  mahakandmisePohjus?: string;
  katkestatud?: string;
  jatkatud?: string;
  katkestusePohjus: string;
  kustutatud: boolean;
  lisainfo?: string;
  tahtaeg?: string;
  lepingud?: Agreement[];
  kasutuslepingud?: Agreement[];
  dokumendid?: Document[];
  oiguslikAlus?: string;
  lisainfoTekst?: string;
  lopetamisePohjus?: string;
  arhiivitoimikuNumber?: string;
  pohimenetlejaId: number;
  pohimenetleja: string;
  saabJatk?: boolean;
}

export interface ProceedingObject {
  id: number;
  varaID: string;
  varaKood: string;
  lisatud: string;
  nimetus: string;
  asukoht: string;
  katastritunnus: KatastridLingiga[];
  lepinguNumber: string;
  volitatudAsutusId: string;
  volitatudAsutus: string;
  valitsemiseesmark: string;
  eesmargiPohjus?: string;
  teostatud: string;
  taotlusteArv: number;
  arvamusteArv: number;
  jatkumenetlusID: string;
  jatkumenetlusNr: string;
  onKinni: boolean;
  pindala: number;
  sihtotstarbed: string;
  objektArveleVoetud?: boolean;
  objektiLiik: string;
  arhiivisVKustutatud?: boolean;
}
export interface ProceedingRelation {
  id: number;
  seotudMenetlus: number;
  number: string;
  menetluseLiik: string;
  alamliik: string;
  seisund: string;
  algatatud: string;
  lopetatud: string;
}
export interface ProceedingCadastral {
  id: number;
  tunnus: string;
  aadress: string;
  pindala?: number;
  sihtotstarve: string;
  lisamiseAeg: string;
  registreerimiseKp: string;
  sulgemiseKp: string;
  omandivorm: string;
  regosaNr: string;
  jatkumenetlusId?: string;
  jatkumenetlusNr?: string;
}
export interface Agreement {
  id: number;
  lepinguNumber?: string;
  liik: string;
  tyyp: string;
  andja?: string[];
  votjad?: string[];
  solmitud?: string;
  kehtibAlates: string;
  kehtibKuni?: string;
  lepinguKood: string;
  alamliik: string;
  seisund: string;
}

export interface Document {
  id: number;
  number: string;
  liik: string;
  alamliik: string;
  nimetus: string;
  aeg: string;
  valjastaja: string;
  url: string;
  adressaat: string;
  markus: string;
  katteKuupaev: string;
  tahtaeg: string;
  failiNimi: string;
  akMarge?: boolean;
}

export interface ChangeProceedingApplicationRequestBody {
  prioriteet: boolean;
  kommentaar: string;
}

export interface ProceedingObjectApplicationRequestBody {
  menetlusobjektid: string[];
  orgID: string | undefined;
  liik: string;
  sisu: string;
  esitaja?: string;
  esitatud?: number;
  keha?: string;
  failNimi?: string;
}

export interface ProceedingObjectApplicationResponseBody {
  id: number;
  liik: string;
  sisu: string;
  esindajaOrganisatsioon: string;
  esitaja: string;
  esitatud: string;
  failinimi: string;
  prioriteet: boolean;
  kommentaar: string;
  onMuudetav: boolean;
}

export interface ProceedingNoticeRequestBody {
  teade: string;
  lisainfo: string;
}

export interface ProceedingDocumentRequestBody {
  nimetus: string;
  kuupaev: number;
  liik: string;
  number: string;
  valjastaja: string;
  url: string;
  failNimi?: string;
  adressaat: string;
  markus: string;
  katteKuupaev: number;
  tahtaeg: number;
  akMarge?: boolean;
  keha?: string;
}

export interface UpdateProceedingDocumentRequestBody {
  updateFields: string[];
  nimetus?: string;
  kuupaev?: number;
  liik?: string;
  number?: string;
  valjastaja?: string;
  url?: string;
  failNimi?: string;
  adressaat?: string;
  markus?: string;
  katteKuupaev?: number;
  tahtaeg?: number;
  akMarge?: boolean;
  keha?: string;
}
export interface ActionHistoryResponseBody {
  limit: number;
  offset: number;
  resultCount: number;
  totalCount: number;
  resultsList: ActionHistory[];
}

export interface ActionHistory {
  id: number;
  aeg: string;
  seosId: number;
  seoseNimetus: string;
  seoseTyyp: string;
  kirjeldus: string;
  muutja: string;
}

export interface ProceedingAssetsResponseBody {
  resultCount: number;
  resultsList: ProceedingAsset[];
  totalCount: number;
}

export interface ProceedingAsset {
  osisId: string;
  seosId: string;
  varaKood: string;
  varaId: string;
  nimetus: string;
  aadress: string;
  valiskood: string;
  varaTyyp: string;
  kehtiv: boolean;
  kuvaLink: boolean;
  varaPindala: string;
}

export interface ProceedingAssetDetailsResponseBody {
  summaTasumisele?: number;
  kasutustasu?: number;
  kasutustasuPeriood?: string;
  hindamised?: Evaluation[];
  menetlusalad?: ProceedingScope[];
}

export interface PutProceedingAssetDetailsBody {
  updateFields: string[];
  summaTasumisele?: number;
  kasutustasu?: number;
  kasutustasuPeriood?: string;
}

export interface Evaluation {
  id: number;
  katriId?: number;
  tellimuseKp?: string;
  vaartuseKp?: string;
  vaartusYhikuKohta?: number;
  yhik?: string;
  hind?: number;
  hinnaMarkused?: string;
  hinnanguKoostaja?: string;
  hinnanguKulu?: number;
  hinnagaNoustumine?: boolean;
}

export interface AddEvaluationBody {
  menetlusobjektId?: number;
  menetlusobjektOsisId?: number;

  tellimuseKp?: string;
  vaartuseKp?: string;
  vaartusYhikuKohta?: number;
  yhik?: string;
  hind?: number;
  hinnaMarkused?: string;
  hinnanguKoostaja?: string;
  hinnanguKulu?: number;
  hinnanguAegumiseKp?: string;
  hinnagaNoustumine?: boolean;
}

export interface EditEvaluationBody extends Omit<AddEvaluationBody, "menetlusobjektId" | "menetlusobjektOsisId"> {
  updateFields: string[];
}

export interface AddRangeOfUseBody {
  ulatus?: string;
  pindala?: number;
  markused?: string;
  katriId?: number;
}

export interface ProceedingScope {
  id: number;
  ulatus?: string;
  pindala?: number;
  markused?: string;
  katriId?: number;
  katriLink?: string;
  katriSooviKuupaev?: string;
}

export interface ProceedingAmortized {
  varaKood: string;
  nimetus: string;
  ehrKood: string;
}

export interface ClassifierRequestBody {
  langCode: string;
  requestedFields: ClassifierRequestType[];
}

export interface AddObjectToProceedingResponseBody {
  menetlusId?: number;
  objektId?: number;
  menetlusObjektId?: number;
}

export interface OrganisationsRequestBody {
  param: string;
  otsiRegistritest?: boolean;
  arhiiv?: boolean;
  regOrgs?: boolean;
  tyyp?: string;
  limit?: number;
}

export interface OrganisationsResponseBody {
  resultCount: number;
  totalCount: number;
  resultsList: Organisation[];
}

export interface ModifyProceedingContactsRequestBody {
  roll?: string;
  nimi?: string;
  epost?: string;
  number?: string;
  postiaadress?: string;
}

export interface Organisation {
  id: number;
  nimi: string;
  registrikood: string;
}

export interface AddExternalOrganisationRequestBody {
  nimi: string;
}

export interface AddExternalOrganisationResponseBody {
  id: string;
  nimi: string;
}

export type LockedBuildingOrganisationsResponseBody = LockedBuildingOrganisation[];
export interface LockedBuildingOrganisation {
  id: number;
  valitsejaId: number;
  valitsejaNimi: string;
  lukustusKuupaev: string;
}

export interface InvestmentApplicationListBody {
  resultsList: InvestmentApplication[];
}

export interface InvestmentApplication {
  id: number;
  kood: string;
  vastavaksTunnistamine?: string;
  esitamine?: string;
  staatus: string;
  taotletavOtsus?: string;
  viimaneOtsus?: string;
  failId?: number;
  failNimi?: string;
  muudetud?: string;
  muutjaNimi?: string;
}

export type ClassifierRequestType =
  | "AUTOMAAGIKA"
  | "EESMARGI_POHJUS_TYPE"
  | "ESTATE_SEARCH_TYPES"
  | "HEITMED_TYPES"
  | "OBJECT_TYPES"
  | "OBJECT_DOCUMENT_TYPES"
  | "ESTATE_PURPOSE_TYPES"
  | "ESTATE_PURPOSE_TYPES_VALITSEMISED"
  | "INVESTEERINGU_LIIK_TYPES"
  | "IP_OTSUS_TYPES"
  | "IP_OTSUSTAJA_TYPES"
  | "IP_STATUS_TYPES"
  | "TAOTLETAV_OTSUS_TYPES"
  | "VARA_LIIK_TYPES"
  | "JUHTFUNKTSIOON_TYPES"
  | "KAIBEMAKSUMAARAD_TYPES"
  | "KVY_ACTION_TYPES"
  | "KVY_ENERGIATOHUSUS"
  | "KVY_FINANTSEERIMISALLIKAS"
  | "KVY_KASUTUSNAITAJAD"
  | "KVY_PINNA_TYYP"
  | "KVY_PLANEERIMISETAPID"
  | "KVY_PRIORITEETSUS"
  | "KVY_SEISUKORD_NR"
  | "KVY_STATUS_TYPES"
  | "KVY_LOPETAMISE_POHJUS"
  | "KVY_KULU_MEETOD"
  | "MENETLUS_STATE_TYPES"
  | "MENETLUS_TYPE"
  | "MENETLUS_TYPE_START"
  | "MENETLUS_TYPE_SUBTYPE"
  | "MENETLUS_VOORANDAMIS_TYPES"
  | "MENETLUS_VOORANDAMIS_TYPES_VOORAND"
  | "MENETLUS_VOORANDAMIS_TYPES_MAAREFORM"
  | "MENETLUS_DOCUMENT_TYPES"
  | "MENETLUS_DOCUMENT_TYPES_OTHER"
  | "MAAREFORM_DOCUMENT_TYPES"
  | "MENETLUS_LOPETAMISE_POHJUS"
  | "MENETLUS_LOPETAMISE_POHJUS_VALDUS"
  | "MENETLUS_LOPETAMISE_POHJUS_OTHER"
  | "MENETLUS_OIGUSLIK_ALUS"
  | "MENETLUS_DETAIL_NOUDED_TAIDETUD"
  | "MENETLUS_DETAIL_TEGEVUSLUBA"
  | "MENETLUS_TEATE_TYPES"
  | "MENETLUSE_ALAMSEISUND_TYPE"
  | "MENETLUSE_EESMARK_TYPE"
  | "MENETLUSE_OSAPOOLED_TYYP"
  | "MENETLUSOBJ_DETAIL_HINNANGU_KOOSTAJA"
  | "MENETLUSOBJ_DETAIL_LOA_VAJADUS"
  | "MENETLUSOBJ_DETAIL_MENETLUSE_POHJUS"
  | "HANKED_KORRALDAJA"
  | "HANKED_LIIK"
  | "HANKED_TULEMUS"
  | "MENETLUSALAD_ULATUS"
  | "HINNAD_KASUTUS_PERIOOD"
  | "MENETLUS_OSAPOOLED_POOL_TYPES"
  | "LEPING_TAHTAEG_LIIK"
  | "MAA_POHIOTSTARVE_TYPE"
  | "MANUS_TYPES"
  | "NOTIFY_TYPE"
  | "NOTIFY_SENDER"
  | "OKSJONID_TULEMUS"
  | "ORGAN_OIGUSLIK_VORM"
  | "LEPING_TYPE"
  | "LEPING_TYPE_SUBTYPE"
  | "LEPING_MAIN_TYPE"
  | "LEPING_SEISUND_TYPES"
  | "LEPING_KASUTUSVIIS"
  | "LISAINFO_TYPE"
  | "LISAINFO_STATUS_TYPE"
  | "MANUAAL_ANALYYS_TYYP"
  | "ANALYYS_TYYP"
  | "PLANEERING_TYYP"
  | "HUVI_TYPE"
  | "TAOTLUSE_TYYP"
  | "LEPINGU_OSAPOOLED_TYYP"
  | "LEPINGU_OSAPOOLED_POOL_TYPES"
  | "CONTRACT_PAYMENT_TYPE"
  | "YYRIPIND_SIHTOTSTARVE_TYPE"
  | "LEPING_DOCUMENT_TYPES"
  | "CONTRACT_MAINTENENCE_WORK_TYPE"
  | "KASUTUSTASU_ALUS"
  | "USER_ROLE_TYPES"
  | "VIIMANE_TOIMING_TYPE"
  | "KAIMASOLEV_TOIMING_TYPES"
  | "OMANDAMISE_OTSUSTAJA_TYPES"
  | "EHITISE_LIIK_TYPES"
  | "MENETLUS_DETAIL_KASUTUSVIIS"
  | "ORGANISATION_TYPES"
  | "ORGANISATION_REG_TYPES"
  | "PIIRANGU_LIIK_TYPE"
  | "SIHTOTSTARVE_TYPE"
  | "KESTVUSPERIOODI_UHIK_TYPE"
  | "YHIK_TYPE"
  | "HOONE_KASUTOTSTARVE_TYPE"
  | "RAJATIS_KASUTOTSTARVE_TYPE"
  | "LOENDI_TYPES"
  | "LEPING_OIGUSLIK_ALUS"
  | "ELUSOLEK_TYPES"
  | "LIHT_KODAKONDSUS_TYPES"
  | "MAKSEGRAAFIKU_STAATUS_TYPES"
  | "TASUMISE_SAGEDUS"
  | "MAKSE_STAATUS_TYPES";

export interface ClassifierResponseBody {
  langCode?: string;
  automaagika?: Classifier[];
  estateSearchTypes?: Classifier[];
  objectTypes?: Classifier[];
  estatePurposeTypes?: Classifier[];
  estatePurposeTypesValitsemised?: Classifier[];
  heitmedTypes?: Classifier[];
  investeeringuLiikTypes?: Classifier[];
  ipOtsusTypes?: Classifier[];
  ipOtsustajaTypes?: Classifier[];
  ipStatusTypes?: Classifier[];
  juhtfunktsioonTypes?: Classifier[];
  kaibemaksumaaradTypes?: Classifier[];
  kvyActionTypes?: Classifier[];
  kvyEnergiatohususTypes?: Classifier[];
  kvyFinantseerimisallikasTypes?: Classifier[];
  kvyKasutusnaitajaTypes?: Classifier[];
  kvyPinnaTyyp?: Classifier[];
  kvyPlaneerimisetapidTypes?: Classifier[];
  kvyPrioriteetsusTypes?: Classifier[];
  kvySeisukordNrTypes?: Classifier[];
  kvyStatusTypes?: Classifier[];
  kvyLopetamisePohjus?: Classifier[];
  kvyVajalikuKuluMeetodTypes?: Classifier[];
  menetlusStateTypes?: Classifier[];
  menetlusType?: Classifier[];
  menetlusTypeStart?: Classifier[];
  menetlusTypeSubtype?: Classifier[];
  menetlusVoorandamisTypes?: Classifier[];
  menetlusVoorandamisTypesVoorand?: Classifier[];
  menetlusVoorandamisTypesMaareform?: Classifier[];
  menetlusDocumentTypes?: Classifier[];
  maareformDocumentTypes?: Classifier[];
  menetlusDocumentTypesOther?: Classifier[];
  menetlusLopetamisePohjus?: Classifier[];
  lopetamisePohjusTypesValdus?: Classifier[];
  lopetamisePohjusTypesOther?: Classifier[];
  menetlusOiguslikAlus?: Classifier[];
  menetlusDetailNoudedTaidetud?: Classifier[];
  menetlusDetailTegevusluba?: Classifier[];
  menetluseAlamseisundTypes?: Classifier[];
  menetluseEesmarkTypes?: Classifier[];
  menetluseOsapooledTyyp?: Classifier[];
  menetlusobjDetailHinnanguKoostaja?: Classifier[];
  menetlusobjDetailLoaVajadus?: Classifier[];
  menetlusobjDetailMenetlusePohjus?: Classifier[];
  menetlusOsapooledPoolTypes?: Classifier[];
  menetlusTeateTypes?: Classifier[];
  hankedKorraldaja?: Classifier[];
  hankedLiik?: Classifier[];
  hankedTulemus?: Classifier[];
  menetlusaladUlatus?: Classifier[];
  hinnadKasutusPeriood?: Classifier[];
  lepingTahtaegLiik?: Classifier[];
  manusTypes?: Classifier[];
  notifyType?: Classifier[];
  notifySender?: Classifier[];
  oksjonidTulemus?: Classifier[];
  organOiguslikVorm?: Classifier[];
  lepingType?: Classifier[];
  lepingTypeSubtype?: Classifier[];
  lepingMainType?: Classifier[];
  lepingSeisundTypes?: Classifier[];
  lepingKasutusviis?: Classifier[];
  lisainfoTypes?: Classifier[];
  lisainfoStatusTypes?: Classifier[];
  maaPohiotstarveType?: Classifier[];
  manuaalAnalyysTyyp?: Classifier[];
  analyysTyyp?: Classifier[];
  planeeringTyyp?: Classifier[];
  huviTyyp?: Classifier[];
  taotluseTyyp?: Classifier[];
  lepinguOsapooledTyyp?: Classifier[];
  lepinguOsapooledPoolTypes?: Classifier[];
  contractPaymentType?: Classifier[];
  yyripindSihtotstarveType?: Classifier[];
  lepingDocumentTypes?: Classifier[];
  contractMaintenanceWorkType?: Classifier[];
  kasutustasuAlusType?: Classifier[];
  userRoleTypes?: Classifier[];
  viimaneToimingType?: Classifier[];
  kaimasolevToimingTypes?: Classifier[];
  omandamiseOtsustajaTypes?: Classifier[];
  ehitiseLiikTypes?: Classifier[];
  menetlusDetailKasutusviis?: Classifier[];
  organisationTypes?: Classifier[];
  organisationRegTypes?: Classifier[];
  piiranguLiikTypes?: Classifier[];
  sihtotstarveTypes?: Classifier[];
  kestvusperioodiYhikTypes?: Classifier[];
  pindalaYhikTypes?: Classifier[];
  hooneKasutusotstarveTypes?: Classifier[];
  rajatisKasutusotstarveTypes?: Classifier[];
  valitsemiseEesmarkPohjusTypes?: Classifier[];
  loendiTypes?: Classifier[];
  taotletavOtsusTypes?: Classifier[];
  varaLiikTypes?: Classifier[];
  lepingOiguslikAlus?: Classifier[];
  elusolekTypes?: Classifier[];
  lihtKodakondsusTypes?: Classifier[];
  maksegraafikuStaatusTypes?: Classifier[];
  tasumiseSagedus?: Classifier[];
  makseStaatusTypes?: Classifier[];
}

export interface GroupedSubtypeClassifierResponseBody {
  [kt: string]: Classifier[];
}

interface Classifier {
  code: string;
  label: string;
}

interface KatastridLingiga {
  katastritunnus: string;
  link: string;
}

export interface ConfigResponseBody {
  jalus?: LinkPair[];
  lubatudFailiLaiendid?: string;
  jkStrateegiaLink?: LinkPair;
  riikideKlassifikaator?: LinkPair;
  adsOidLink?: LinkPair;
  ehrLink?: string;
  krLink?: string;
}

export interface LinkPair {
  text: string;
  link: string;
  id?: number;
}

export interface FileRequestBody {
  id: number;
  tabel: TableNames;
}

export interface FileResponseBody {
  filename: string;
}

type TableNames =
  | "LEPINGU_DOKUMENDID"
  | "MENETLUSDOKUMENDID"
  | "OBJEKTI_LISAINFO"
  | "MANUSED"
  | "OBJEKTI_MANUSED"
  | "KVV_TAOTLUSED"
  | "HALDUSDOKUMENDID"
  | "KVY_FAILID"
  | "INVESTEERINGUPROJEKTI_FAILID"
  | "IP_OTSUSE_FAILID"
  | "INVESTEERINGUTAOTLUSE_FAIL";

export type LoendiTypes =
  | "PLAANID"
  | "YLEVAATUSED"
  | "HOOLDUSED"
  | "HALDURITASE"
  | "USALDUSVAARSUS"
  | "KORRALDUSMUDEL"
  | "KKMPIIRAV"
  | "ENERGIALLIKAS"
  | "HOONESISUND";

export interface ManagersResponseBody {
  resultsList: NameAndId[];
}

export interface NameAndIdResponseBody {
  resultsList: NameAndId[];
}
export interface OrgModelResponseBody {
  resultsList: NimetusAndId[];
}
export interface NimetusAndId {
  id: string;
  nimetus: string;
}

export interface ManagerResponseBody extends NameAndId {}

export interface AuthorizedBodiesResponseBody {
  resultsList: NameAndId[];
}

export interface NameAndId {
  id: number;
  nimi: string;
}

export interface Timerange {
  from?: string;
  to?: string;
}

export interface TableResponse<T> {
  offset: number;
  limit: number;
  totalCount: number;
  resultCount: number;
  resultsList: T[];
}

export interface CadastralAssetCost {
  maksustamishind: number | null;
}

interface Error {
  errorParam: string;
  errorCode: string;
}

export { post, get, put, remove, untypedGet };
